import {TENANT_BASE_URL} from "../../../common/Config/UrlHelper";
import DatatableHelperMixin from "../../../common/Mixin/Global/DatatableHelperMixin";
import {formatDateToLocal} from "../../../common/Helper/Support/DateTimeHelper";
import HelperMixin from "../../../common/Mixin/Global/HelperMixin";
import {numberFormatter} from "../../../common/Helper/Support/SettingsHelper";
import {axiosGet} from "../../../common/Helper/AxiosHelper";

export default {
    mixins: [HelperMixin, DatatableHelperMixin],
    data() {
        return {
            categories: [],
            options: {
                name             : this.$t('transaction_imports'),
                url              : `${TENANT_BASE_URL}app/finance-transaction-imports`,
                showHeader       : true,
                showCount        : true,
                tableShadow      : false,
                tablePaddingClass: 'pt-primary',
                columns          : [
                    {
                        title    : this.$t('transaction_date'),
                        type     : 'custom-html',
                        key      : 'transaction_date',
                        isVisible: true,
                        modifier : (value) => {
                            return value ? formatDateToLocal(value) : '--';
                        }
                    },
                    {
                        title    : this.$t('transaction_details'),
                        type     : 'text',
                        key      : 'transaction_details',
                        isVisible: true,
                    },
                    {
                        title    : this.$t('withdrawal_amount'),
                        type     : 'custom-html',
                        key      : 'withdrawal_amount',
                        isVisible: true,
                        modifier : (value, row) => {
                            return this.formatAmount(value);
                        }
                    },
                    {
                        title    : this.$t('deposit_amount'),
                        type     : 'custom-html',
                        key      : 'deposit_amount',
                        isVisible: true,
                        modifier : (value, row) => {
                            return this.formatAmount(value);
                        }
                    },
                    {
                        title    : this.$t('balance'),
                        type     : 'custom-html',
                        key      : 'balance',
                        isVisible: true,
                        modifier : (value, row) => {
                            return this.formatAmount(value);
                        }
                    },
                    {
                        title    : this.$t('description'),
                        type     : 'custom-html',
                        key      : 'description',
                        isVisible: true,
                        modifier : (value, row) => {
                            const disabled = (row.status === 'error' || row.status === 'converted') ? 'disabled' : '';
                            return `<input type="text" class="form-control form-control-sm editable-description" 
                                data-row-id="${row.id}" 
                                value="${value || ''}" 
                                placeholder="${this.$t('description')}"
                                ${disabled}
                                style="min-width: 150px;" />`;
                        }
                    },
                    {
                        title    : this.$t('category'),
                        type     : 'custom-html',
                        key      : 'category_id',
                        isVisible: true,
                        modifier : (value, row) => {
                            // Filter categories by row type (income/expense)
                            const filteredCategories = this.categories.filter(cat => !row.type || cat.type === row.type);
                            let options = '<option value="" selected disabled>--</option>';
                            filteredCategories.forEach(cat => {
                                const selected = value == cat.id ? 'selected' : '';
                                options += `<option value="${cat.id}" ${selected}>${cat.name}</option>`;
                            });
                            const disabled = (row.status === 'error' || row.status === 'converted') ? 'disabled' : '';
                            return `<select class="form-control form-control-sm editable-category" 
                                data-row-id="${row.id}" 
                                ${disabled}
                                style="min-width: 150px;">${options}</select>`;
                        }
                    },
                    {
                        title    : this.$t('tags'),
                        type     : 'component',
                        key      : 'tags',
                        isVisible: true,
                        componentName: 'app-finance-tag-cell'
                    },
                    {
                        title    : this.$t('status'),
                        type     : 'custom-html',
                        key      : 'status',
                        isVisible: true,
                        modifier : (value, row) => {
                            return this.getStatusBadge(value, row.message);
                        }
                    },
                    {
                        title    : this.$t('message'),
                        type     : 'custom-html',
                        key      : 'message',
                        isVisible: true,
                        modifier : (value) => {
                            return value ? this.$t(value) : '--';
                        }
                    },
                    {
                        title    : this.$t('created_at'),
                        type     : 'custom-html',
                        key      : 'created_at',
                        isVisible: false,
                        modifier : (value) => {
                            return value ? formatDateToLocal(value) : '--';
                        }
                    },
                ],
                filters          : [
                    {
                        title         : this.$t('search'),
                        type          : "text",
                        key           : "search",
                        placeholder   : this.$t('search')
                    },
                    {
                        title         : this.$t('status'),
                        type          : "dropdown-menu-filter",
                        key           : "status",
                        option        : [
                            {id: 'pending', value: this.$t('pending')},
                            {id: 'error', value: this.$t('error')},
                            {id: 'confirmed', value: this.$t('confirmed')},
                            {id: 'converted', value: this.$t('converted')},
                        ],
                        listValueField: 'value',
                    },
                    {
                        title         : this.$t('bank_code'),
                        type          : "text",
                        key           : "bank_code",
                        placeholder   : this.$t('bank_code')
                    },
                    {
                        title : this.$t('transaction_date'),
                        type  : "range-picker",
                        key   : "transaction_date",
                        option: ["today", "thisMonth", "last7Days", "thisYear"]
                    },
                ],
                paginationType   : "pagination",
                responsive       : true,
                rowLimit         : 15,
                showAction       : false,
                orderBy          : 'desc',
                actionType       : 'dropdown',
                actions          : [
                    {
                        title     : this.$t('confirm'),
                        icon      : 'check',
                        type      : 'modal',
                        name      : 'confirm',
                        modifier  : row => row.status === 'pending'
                    },
                    {
                        title     : this.$t('edit'),
                        icon      : 'edit',
                        type      : 'modal',
                        name      : 'edit',
                        modifier  : row => row.status === 'pending' || row.status === 'error'
                    },
                    {
                        title    : this.$t('delete'),
                        icon     : 'trash-2',
                        type     : 'modal',
                        component: 'app-confirmation-modal',
                        modalId  : 'app-confirmation-modal',
                        url      : `${TENANT_BASE_URL}app/finance-transaction-imports/`,
                        name     : 'delete',
                        modifier : row => row.status === 'pending' || row.status === 'error'
                    }
            ]
            }
        }
    },
    methods: {
        loadCategories() {
            const locale = window?.appLanguage?.toLowerCase() || 'ja';
            axiosGet(`${TENANT_BASE_URL}app/finance-categories?all=1&locale=${locale}`)
                .then(({data}) => {
                    this.categories = data.map(category => {
                        return {
                            ...category,
                            name: this.$displayByLocale ? this.$displayByLocale(category.name) : category.name
                        }
                    });
                    this.$hub.$emit('reload-finance-transaction-import-table');
                })
        },
        formatAmount(value) {
            if (!value || value == 0) return '--';
            const settings = window.settings || {};
            const amount = numberFormatter(Number(value));
            const symbol = settings.currency_symbol || '';
            const position = settings.currency_position || 'left';
            return this.$t(`format_${position}`, {symbol, amount});
        },
        getStatusBadge(status, errorMessage) {
            const badges = {
                'pending': `<span class="badge badge-pill badge-warning">${this.$t('pending')}</span>`,
                'error': `<span class="badge badge-pill badge-danger" title="${errorMessage ? this.$t(errorMessage) : this.$t('error')}">${this.$t('error')}</span>`,
                'confirmed': `<span class="badge badge-pill badge-success">${this.$t('confirmed')}</span>`,
                'converted': `<span class="badge badge-pill badge-info">${this.$t('converted')}</span>`,
            };
            return badges[status] || status;
        }
    }
}
